<template>
  <div class="landing-page login-page">
    <!-- 主内容：左侧 Hero 文案 + 右侧登录卡片（与 Landing 区块风格一致） -->
    <main class="login-main">
      <div class="left">
        <img src="../../assets/teacher.png"  class="logo-img"/>
        <section class="login-hero">
          <p class="hero-pretitle">AI-Powered Learning Platform</p>
          <h1 class="hero-title">AI-Powered Personalized Math Learning</h1>
          <p class="hero-subtitle">MYTutor helps students master mathematics through intelligent diagnostics, adaptive learning paths, and targeted practice.</p>
        </section>
      </div>

      <section class="login-card-wrap">
        <div class="login-card">
          <h2 class="login-card-title">{{ $t('tutor-login.loginBtn') }}</h2>
          <el-form :model="loginForm" :rules="rules" ref="loginForm" class="login-form">
            <el-form-item prop="userName">
              <el-input
                v-model="loginForm.userName"
                :placeholder="$t('tutor-login.usernamePlaceholder')"
                class="get-started-input"
              />
            </el-form-item>
            <el-form-item prop="password">
              <el-input
                v-model="loginForm.password"
                type="password"
                :placeholder="$t('tutor-login.passwordPlaceholder')"
                show-password
                class="get-started-input"
              />
            </el-form-item>
            <el-form-item prop="captcha" class="login-captcha-item">
              <el-input
                v-model="loginForm.captcha"
                :placeholder="$t('tutor-login.captchaPlaceholder')"
                class="get-started-input get-started-input-captcha"
              >
                <div class="login-code" slot="suffix">
                  <el-image
                    :src="captchaInfo.captchaImage"
                    @click="getCaptcha"
                    alt="captcha"
                    style="width: 100%; height: 100%; cursor: pointer"
                  >
                    <div slot="error" class="image-error-slot">
                      <i class="el-icon-loading"></i> {{ $t('tutor-login.loading') }}
                    </div>
                  </el-image>
                </div>
              </el-input>
            </el-form-item>
          </el-form>
          <div class="login-form-actions">
            <button type="button" class="login-forgot-link" @click="openResetPassword">
              {{ $t('tutor-login.forgotPassword') }}
            </button>
          </div>
          <button type="button" class="btn-get-started" @click="handleLogin">
            {{ $t('tutor-login.loginBtn') }}
          </button>
          <p class="login-register">
            <span class="login-register-text">{{ noAccountText }}</span>
            <button type="button" class="login-register-link" @click="toRegister">{{ $t('tutor-login.register') }}</button>
          </p>
        </div>
      </section>
    </main>

    <footer class="landing-footer login-footer">
      <div class="footer-inner">
        <div class="footer-bar">
          <span class="footer-copy">{{ $t('tutor-login.footer') }}</span>
          <a @click="$router.push('/about')" target="_blank" rel="noopener" class="footer-legal-link">MYTEC</a>
        </div>
      </div>
    </footer>

    <el-dialog
      :visible.sync="resetPasswordVisible"
      width="480px"
      custom-class="login-reset-dialog-modern"
      :show-close="false"
      :append-to-body="true"
      :close-on-click-modal="false"
      @close="closeResetPassword"
    >
      <div slot="title" class="login-reset-dialog-header">
        <div class="login-reset-dialog-header-title">
          <i class="el-icon-key" aria-hidden="true" />
          <span>{{ $t('tutor-login.resetPasswordTitle') }}</span>
        </div>
        <button type="button" class="login-reset-dialog-close" :aria-label="$t('tutor-login.backToLogin')" @click="closeResetPassword">
          <i class="el-icon-close" />
        </button>
      </div>

      <div class="login-reset-dialog-body">
        <div class="login-reset-info">
          <i class="el-icon-message" aria-hidden="true" />
          <p>{{ $t('tutor-login.resetPasswordHint') }}</p>
        </div>

        <el-form
          ref="resetPasswordForm"
          :model="resetPasswordForm"
          :rules="resetPasswordRules"
          label-position="top"
          class="login-reset-form"
        >
          <el-form-item prop="userAccount">
            <template slot="label">
              <span class="login-reset-label">{{ $t('tutor-register.username') }}</span>
            </template>
            <el-input
              v-model="resetPasswordForm.userAccount"
              :placeholder="$t('tutor-login.usernamePlaceholder')"
              prefix-icon="el-icon-user"
              class="login-reset-input"
            />
          </el-form-item>

          <el-form-item prop="verificationCode">
            <template slot="label">
              <span class="login-reset-label">{{ $t('validation.enterVerificationCode') }}</span>
            </template>
            <div class="login-reset-code-row">
              <el-input
                v-model="resetPasswordForm.verificationCode"
                maxlength="6"
                :placeholder="$t('validation.sixDigitCode')"
                prefix-icon="el-icon-message"
                class="login-reset-input login-reset-code-input"
              />
              <button
                type="button"
                class="login-reset-code-btn"
                :disabled="resetCodeCountdown > 0 || resetCodeSending"
                @click="sendResetCode"
              >
                <i v-if="resetCodeSending" class="el-icon-loading" aria-hidden="true" />
                <span>{{ resetCodeSending ? $t('tutor-login.loading') : (resetCodeCountdown > 0 ? `${resetCodeCountdown}s` : $t('tutor-login.sendVerificationCode')) }}</span>
              </button>
            </div>
          </el-form-item>

          <el-form-item prop="password">
            <template slot="label">
              <span class="login-reset-label">{{ $t('register.password') }}</span>
            </template>
            <el-input
              v-model="resetPasswordForm.password"
              type="password"
              show-password
              :placeholder="$t('tutor-login.passwordPlaceholder')"
              prefix-icon="el-icon-lock"
              class="login-reset-input"
            />
          </el-form-item>

          <el-form-item prop="rePassword">
            <template slot="label">
              <span class="login-reset-label">{{ $t('register.confirmPassword') }}</span>
            </template>
            <el-input
              v-model="resetPasswordForm.rePassword"
              type="password"
              show-password
              :placeholder="$t('register.confirmPassword')"
              prefix-icon="el-icon-lock"
              class="login-reset-input"
            />
          </el-form-item>
        </el-form>
      </div>

      <span slot="footer" class="login-reset-dialog-footer">
        <button type="button" class="login-reset-btn-cancel" @click="closeResetPassword">
          {{ $t('tutor-login.backToLogin') }}
        </button>
        <button type="button" class="login-reset-btn-save" :disabled="resetPasswordSaving" @click="confirmResetPassword">
          <i v-if="resetPasswordSaving" class="el-icon-loading" aria-hidden="true" />
          <i v-else class="el-icon-check" aria-hidden="true" />
          {{ $t('tutor-login.resetPasswordBtn') }}
        </button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getLoginCaptcha } from '@/api/tutor'
import { encrypt } from '@/utils/crypto'
import { login, sendResetPasswordCode, resetPassword } from '@/api/user'

export default {
  name: 'login',
  data () {
    const validateResetPasswordMatch = (rule, value, callback) => {
      if (value !== this.resetPasswordForm.password) {
        callback(new Error(this.$t('validation.passwordMismatch')))
      } else {
        callback()
      }
    }
    return {
      loginForm: {
        userName: '',
        password: '',
        captcha: ''
      },
      resetPasswordVisible: false,
      resetPasswordSaving: false,
      resetCodeSending: false,
      resetCodeCountdown: 0,
      resetCodeTimer: null,
      resetPasswordForm: {
        userAccount: '',
        verificationCode: '',
        password: '',
        rePassword: ''
      },
      resetPasswordRules: {
        userAccount: [
          { required: true, message: '', trigger: 'blur' },
          { min: 3, max: 20, message: '', trigger: 'blur' }
        ],
        verificationCode: [
          { required: true, message: '', trigger: 'blur' },
          { len: 6, message: '', trigger: 'blur' }
        ],
        password: [
          { required: true, message: '', trigger: 'blur' },
          { min: 6, message: '', trigger: 'blur' }
        ],
        rePassword: [
          { required: true, message: '', trigger: 'blur' },
          { validator: validateResetPasswordMatch, trigger: 'blur' }
        ]
      },
      rules: {
        userName: [
          { required: true, message: 'Please enter username', trigger: 'blur' },
          { min: 3, max: 20, message: 'Length 3-20', trigger: 'blur' }
        ],
        password: [
          { required: true, message: 'Please enter password', trigger: 'blur' },
          { min: 6, message: 'Min 6 characters', trigger: 'blur' }
        ],
        captcha: [
          { required: true, message: 'Please enter captcha', trigger: 'blur' }
        ]
      },
      captchaInfo: {}
    }
  },
  computed: {
    noAccountText () {
      const t = this.$t('tutor-login.noAccount')
      return (t && t !== 'tutor-login.noAccount') ? t : "Don't have an account yet?"
    }
  },
  mounted () {
    this.getCaptcha()
    this.initRulesI18n()
  },
  beforeDestroy () {
    this.clearResetCodeTimer()
  },
  methods: {
    initRulesI18n () {
      this.rules.userName[0].message = this.$t('tutor-login.enterUserAccount')
      this.rules.userName[1].message = this.$t('tutor-login.length3to20')
      this.rules.password[0].message = this.$t('tutor-login.enterPassword')
      this.rules.password[1].message = this.$t('tutor-login.passwordMin6')
      this.rules.captcha[0].message = this.$t('tutor-login.enterCaptcha')
      this.resetPasswordRules.userAccount[0].message = this.$t('tutor-login.enterUserAccount')
      this.resetPasswordRules.userAccount[1].message = this.$t('tutor-login.length3to20')
      this.resetPasswordRules.verificationCode[0].message = this.$t('validation.enterVerificationCode')
      this.resetPasswordRules.verificationCode[1].message = this.$t('validation.sixDigitCode')
      this.resetPasswordRules.password[0].message = this.$t('tutor-login.enterPassword')
      this.resetPasswordRules.password[1].message = this.$t('tutor-login.passwordMin6')
      this.resetPasswordRules.rePassword[0].message = this.$t('validation.enterPasswordAgain')
    },
    getCaptcha () {
      getLoginCaptcha().then((res) => {
        if (res.code === 200) {
          this.captchaInfo = res.data
        }
      })
    },
    handleLogin () {
      this.$refs.loginForm.validate((valid) => {
        if (!valid) return false
        const params = {
          userAccount: this.loginForm.userName,
          password: encrypt(this.loginForm.password),
          captcha: this.loginForm.captcha,
          captchaId: this.captchaInfo.captchaId
        }
        login(params).then((res) => {
          if (res.code !== 200) {
            this.$message.error(res.message)
            this.getCaptcha()
            return false
          }
          this.$message.success('Login Successful')
          this.$store.dispatch('user/loginSuccess', { user: res.data })
          const redirect = this.$route.query.redirect
          if (typeof redirect === 'string') {
            const resolvedRoute = this.$router.resolve(redirect).route
            if (resolvedRoute.matched.some(record => record.meta.requiresAuth)) {
              this.$router.replace(redirect)
              return
            }
          }
          if (res.data.role === 'S') {
            this.$router.push({ path: '/studentHomepage' })
          } else if (res.data.type === 9) {
            this.$router.push({ path: '/teacherManagement' })
          } else {
            this.$router.push({ path: '/parentOrTeacher' })
          }
        })
      })
    },
    toRegister () {
      this.$router.push({ path: '/registerNew' })
    },
    openResetPassword () {
      this.resetPasswordForm = {
        userAccount: this.loginForm.userName || '',
        verificationCode: '',
        password: '',
        rePassword: ''
      }
      this.resetPasswordVisible = true
      this.$nextTick(() => {
        this.$refs.resetPasswordForm && this.$refs.resetPasswordForm.clearValidate()
      })
    },
    clearResetCodeTimer () {
      if (this.resetCodeTimer) {
        clearInterval(this.resetCodeTimer)
        this.resetCodeTimer = null
      }
      this.resetCodeCountdown = 0
    },
    closeResetPassword () {
      this.resetPasswordVisible = false
      this.resetPasswordSaving = false
      this.resetCodeSending = false
      this.clearResetCodeTimer()
      this.resetPasswordForm = {
        userAccount: '',
        verificationCode: '',
        password: '',
        rePassword: ''
      }
      this.$refs.resetPasswordForm && this.$refs.resetPasswordForm.resetFields()
    },
    sendResetCode () {
      this.$refs.resetPasswordForm.validateField('userAccount', (error) => {
        if (error) return
        this.resetCodeSending = true
        sendResetPasswordCode({
          userAccount: this.resetPasswordForm.userAccount.trim()
        }).then((res) => {
          if (res.code !== 200) {
            this.$message.error(res.msg || this.$t('tutor-login.resetPasswordFailed'))
            return
          }
          this.$message.success(this.$t('register.msg.codeSent'))
          this.clearResetCodeTimer()
          this.resetCodeCountdown = 60
          this.resetCodeTimer = setInterval(() => {
            if (this.resetCodeCountdown <= 1) {
              this.clearResetCodeTimer()
            } else {
              this.resetCodeCountdown -= 1
            }
          }, 1000)
        }).catch(() => {
          this.$message.error(this.$t('tutor-login.resetPasswordFailed'))
        }).finally(() => {
          this.resetCodeSending = false
        })
      })
    },
    confirmResetPassword () {
      this.$refs.resetPasswordForm.validate((valid) => {
        if (!valid) return
        this.resetPasswordSaving = true
        resetPassword({
          userAccount: this.resetPasswordForm.userAccount.trim(),
          verificationCode: this.resetPasswordForm.verificationCode,
          password: encrypt(this.resetPasswordForm.password)
        }).then((res) => {
          if (res.code !== 200) {
            this.$message.error(res.msg || this.$t('tutor-login.resetPasswordFailed'))
            return
          }
          this.$message.success(this.$t('tutor-login.resetPasswordSuccess'))
          this.loginForm.userName = this.resetPasswordForm.userAccount.trim()
          this.loginForm.password = ''
          this.closeResetPassword()
          this.getCaptcha()
        }).catch(() => {
          this.$message.error(this.$t('tutor-login.resetPasswordFailed'))
        }).finally(() => {
          this.resetPasswordSaving = false
        })
      })
    }
  }
}
</script>

<style lang="scss" scoped>
/* 与 Landing 完全一致的变量与基础 */
.landing-page {
  --lp-section-pad: 64px;
  --lp-gap: 24px;
  --lp-gap-sm: 12px;
  --lp-card-pad: 24px;
  --lp-radius: 12px;
  --lp-radius-sm: 8px;
  --lp-max: 1200px;
  --lp-margin-vw: 6%;
  font-family: 'Ubuntu', -apple-system, BlinkMacSystemFont, sans-serif;
  background: #f9fafc;
  color: #1e293b;
  min-height: 100vh;
  overflow-x: hidden;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.landing-header {
  position: sticky;
  top: 0;
  z-index: 100;
  margin-left: var(--lp-margin-vw);
  margin-right: var(--lp-margin-vw);
  padding: 12px 0;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  width: calc(100% - 2 * var(--lp-margin-vw));
  box-sizing: border-box;
}
.header-inner {
  max-width: var(--lp-max);
  margin: 0 auto;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
}
.header-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: inherit;
}
.header-logo-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #86efac 0%, #4ade80 100%);
  flex-shrink: 0;
  display: block;
}
.header-logo-text { font-size: 1.125rem; letter-spacing: -0.01em; }
.header-logo-bold { font-weight: 700; color: #0f172a; }
.header-logo-light { font-weight: 400; color: #64748b; }
.header-brand:hover .header-logo-bold { color: #2563eb; }
.header-brand:hover .header-logo-light { color: #475569; }
.header-nav { display: flex; align-items: center; gap: 0; }
.header-link {
  font-size: 0.9375rem;
  font-weight: 400;
  color: #475569;
  text-decoration: none;
  padding: 0 14px;
  transition: color 0.2s;
}
.header-link:hover { color: #0f172a; }
.header-sep { width: 1px; height: 14px; background: #e2e8f0; flex-shrink: 0; }
.header-cta { display: flex; align-items: center; }
.btn {
  padding: 12px 24px;
  border-radius: var(--lp-radius-sm);
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  border: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  position: relative;
  overflow: hidden;
}
.btn-cta-nav {
  padding: 10px 22px;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  background: linear-gradient(90deg, #f97316 0%, #ef4444 100%);
  transition: opacity 0.2s, transform 0.2s;
}
.btn-cta-nav:hover { opacity: 0.95; transform: translateY(-1px); }

@media (max-width: 768px) {
  .header-nav { display: none; }
}

.login-main {
  flex: 1;
  margin-left: var(--lp-margin-vw);
  margin-right: var(--lp-margin-vw);
  width: calc(100% - 2 * var(--lp-margin-vw));
  padding: var(--lp-section-pad) 0;
  box-sizing: border-box;
  max-width: var(--lp-max);
  margin-left: auto;
  margin-right: auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  align-items: center;
}
.left{
  flex-direction: column;
  display: flex;
  margin-top: -24px;
  img {
    width: 48px;
    height: 48px;
    margin-bottom: 12px;
  }
}
.login-hero {
  text-align: left;
}
.hero-pretitle {
  font-size: 0.9375rem;
  color: #475569;
  margin: 0 0 var(--lp-gap-sm) 0;
}
.hero-title {
  font-size: clamp(1.75rem, 4vw, 2.75rem);
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 var(--lp-gap-sm) 0;
  line-height: 1.25;
}
.hero-subtitle {
  font-size: 1.125rem;
  color: #475569;
  margin: 0 0 var(--lp-gap) 0;
  line-height: 1.5;
}
.login-hero-desc {
  font-size: 1rem;
  color: #64748b;
  margin: 0 0 var(--lp-gap-sm) 0;
  line-height: 1.6;
}
.hero-bullets {
  margin: 0;
  padding-left: 20px;
  font-size: 0.9375rem;
  color: #64748b;
  line-height: 1.7;
}
.hero-bullets li { margin-top: 4px; }

.login-card-wrap {
  display: flex;
  justify-content: center;
  align-items: flex-start;
}
.login-card {
  background: #fff;
  padding: var(--lp-card-pad);
  border-radius: var(--lp-radius);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  width: 100%;
  max-width: 400px;
}
.login-card-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 20px 0;
}

/* 表单项统一间距 */
.login-form ::v-deep .el-form-item {
  margin-bottom: 16px;
}

.login-form ::v-deep .el-form-item:last-child {
  margin-bottom: 0;
}

.login-form-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
}

.login-forgot-link {
  background: none;
  border: none;
  padding: 0;
  color: #7c3aed;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  line-height: 1.4;
  transition: color 0.2s;
}

.login-forgot-link:hover {
  color: #6d28d9;
  text-decoration: underline;
}

.get-started-input ::v-deep .el-input__inner {
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #e2e8f0;
  border-radius: var(--lp-radius-sm);
  font-size: 0.9375rem;
  background: #fff;
  font-family: inherit;
  transition: border-color 0.2s;
}
.get-started-input ::v-deep .el-input__inner::placeholder {
  color: #94a3b8;
}
.get-started-input ::v-deep .el-input__inner:focus {
  outline: none;
  border-color: #8b5cf6;
}
.get-started-input ::v-deep .el-input__inner:hover {
  border-color: #c4b5fd;
}
.login-captcha-item ::v-deep .el-input__inner {
  padding-right: 130px;
}
.login-code {
  height: 38px;
  width: 120px;
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  border-radius: 6px;
  overflow: hidden;
}
.image-error-slot {
  text-align: center;
  background: #f1f5f9;
  height: 100%;
  font-size: 12px;
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-get-started {
  width: 100%;
  padding: 14px 24px;
  border: none;
  border-radius: 999px;
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(to right, #8b5cf6 0%, #6366f1 50%, #3b82f6 100%);
  cursor: pointer;
  transition: opacity 0.2s, transform 0.2s;
  margin-top: 16px;
  font-family: inherit;
}
.btn-get-started:hover {
  opacity: 0.95;
  transform: translateY(-1px);
}

.login-register {
  margin: 16px 0 0 0;
  text-align: center;
  font-size: 0.9375rem;
  color: #64748b;
}
.login-register-link {
  background: none;
  border: none;
  padding: 0;
  margin-left: 4px;
  color: #7c3aed;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  font-size: inherit;
}
.login-register-link:hover {
  text-decoration: underline;
}

.landing-footer {
  background: #101010;
  color: #fff;
  width: 100%;
  padding: var(--lp-gap) 0;
  margin-top: 0;
  box-sizing: border-box;
}
.footer-inner { max-width: var(--lp-max); margin: 0 auto; width: 100%; }
.footer-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: var(--lp-gap-sm);
}
.footer-copy { font-size: 0.8125rem; color: rgba(255, 255, 255, 0.6); }
.footer-legal-link {
  font-size: 0.8125rem;
  color: rgba(255, 255, 255, 0.6);
  text-decoration: none;
  transition: color 0.2s;
}
.footer-legal-link:hover { color: rgba(255, 255, 255, 0.9); }

@media (max-width: 900px) {
  .login-main {
    grid-template-columns: 1fr;
    gap: 32px;
  }
  .login-hero { text-align: center; }
  .login-card-wrap { justify-content: center; }
  .login-card { margin: 0 auto; }
}
@media (max-width: 600px) {
  .login-main { padding: 40px 0; }
  .login-card { padding: var(--lp-gap); }
}
</style>

<style lang="scss">
/* append-to-body 弹窗：与 ToolBar Profile 弹窗、登录页渐变风格统一 */
.el-dialog.login-reset-dialog-modern {
  border-radius: 20px !important;
  overflow: hidden;
  font-family: 'Ubuntu', -apple-system, BlinkMacSystemFont, sans-serif;
  box-shadow: 0 24px 64px rgba(15, 23, 42, 0.14), 0 12px 28px rgba(99, 102, 241, 0.18) !important;
}

.el-dialog.login-reset-dialog-modern .el-dialog__header {
  padding: 0 !important;
  border-bottom: none !important;
}

.el-dialog.login-reset-dialog-modern .el-dialog__body {
  padding: 20px 24px 8px !important;
  background: #fff;
}

.el-dialog.login-reset-dialog-modern .el-dialog__footer {
  padding: 16px 24px 22px !important;
  border-top: 1px solid #e5e7eb;
  background: #fff;
}

.login-reset-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px;
  background: linear-gradient(135deg, #7c3aed 0%, #6366f1 50%, #3b82f6 100%);
  color: #fff;
}

.login-reset-dialog-header-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 17px;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.login-reset-dialog-header-title i {
  font-size: 20px;
  opacity: 0.95;
}

.login-reset-dialog-close {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: background 0.2s;
}

.login-reset-dialog-close:hover {
  background: rgba(255, 255, 255, 0.28);
}

.login-reset-dialog-close i {
  font-size: 16px;
  font-weight: 700;
}

.login-reset-info {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 20px;
  padding: 14px 16px;
  border-radius: 12px;
  background: linear-gradient(135deg, #f5f3ff 0%, #eef2ff 100%);
  border: 1px solid #ddd6fe;
}

.login-reset-info i {
  flex-shrink: 0;
  margin-top: 2px;
  font-size: 18px;
  color: #7c3aed;
}

.login-reset-info p {
  margin: 0;
  font-size: 13px;
  line-height: 1.55;
  color: #475569;
}

.login-reset-form .el-form-item {
  margin-bottom: 18px;
}

.login-reset-form .el-form-item__label {
  padding: 0 !important;
  line-height: 1.2 !important;
}

.login-reset-form .el-form-item__content {
  margin-left: 0 !important;
}

.login-reset-form .el-form-item__label::before {
  color: #7c3aed !important;
}

.login-reset-label {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
  margin-bottom: 4px;
}

.login-reset-form .el-input .el-input__prefix {
  left: 12px;
  color: #94a3b8;
}

.login-reset-form .login-reset-input .el-input__inner {
  height: 44px !important;
  padding-left: 40px !important;
  border-radius: 10px !important;
  border: 1px solid #cbd5e1 !important;
  background: #fff !important;
  font-size: 14px;
  color: #0f172a;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.login-reset-form .login-reset-input .el-input__inner::placeholder {
  color: #94a3b8;
}

.login-reset-form .login-reset-input .el-input__inner:hover {
  border-color: #c4b5fd !important;
}

.login-reset-form .login-reset-input .el-input__inner:focus {
  border-color: #8b5cf6 !important;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.15);
}

.login-reset-code-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.login-reset-code-input {
  flex: 1;
  min-width: 0;
}

.login-reset-code-btn {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 118px;
  height: 44px;
  padding: 0 14px;
  border: none;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  background: linear-gradient(90deg, #8b5cf6 0%, #6366f1 100%);
  cursor: pointer;
  font-family: inherit;
  box-shadow: 0 6px 16px rgba(99, 102, 241, 0.28);
  transition: opacity 0.2s, transform 0.2s, box-shadow 0.2s;
}

.login-reset-code-btn:hover:not(:disabled) {
  opacity: 0.95;
  transform: translateY(-1px);
}

.login-reset-code-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  box-shadow: none;
  transform: none;
}

.login-reset-dialog-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.login-reset-btn-cancel {
  min-width: 120px;
  height: 42px;
  padding: 0 18px;
  border-radius: 999px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #475569;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.2s, border-color 0.2s;
}

.login-reset-btn-cancel:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.login-reset-btn-save {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 168px;
  height: 42px;
  padding: 0 20px;
  border: none;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 700;
  color: #fff;
  background: linear-gradient(90deg, #8b5cf6 0%, #6366f1 50%, #3b82f6 100%);
  cursor: pointer;
  font-family: inherit;
  box-shadow: 0 8px 20px rgba(99, 102, 241, 0.32);
  transition: opacity 0.2s, transform 0.2s;
}

.login-reset-btn-save:hover:not(:disabled) {
  opacity: 0.95;
  transform: translateY(-1px);
}

.login-reset-btn-save:disabled {
  opacity: 0.65;
  cursor: not-allowed;
  transform: none;
}

@media (max-width: 520px) {
  .login-reset-code-row {
    flex-direction: column;
  }

  .login-reset-code-btn {
    width: 100%;
  }

  .login-reset-dialog-footer {
    flex-direction: column-reverse;
  }

  .login-reset-btn-cancel,
  .login-reset-btn-save {
    width: 100%;
  }
}
</style>
